import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { apiClient, ErreurApi, type IdentiteClient } from "@/client/api";

/**
 * L'identité du CLIENT, et rien d'autre.
 *
 * Ce contexte ne porte ni profil, ni permission, ni identifiant d'utilisateur
 * interne : un client n'en a pas. Les deux fournisseurs de session ne sont
 * jamais montés en même temps — `main.tsx` choisit l'un OU l'autre selon
 * l'adresse — de sorte qu'un écran client ne peut pas, même par erreur, lire
 * une identité interne.
 */

type EtatClient =
  | { statut: "chargement" }
  | { statut: "anonyme" }
  | { statut: "connecte"; identite: IdentiteClient };

type Contexte = EtatClient & {
  connexion: (email: string, motDePasse: string) => Promise<void>;
  deconnexion: () => Promise<void>;
};

const ContexteClient = createContext<Contexte | null>(null);

export function FournisseurSessionClient({ children }: { children: ReactNode }) {
  const [etat, setEtat] = useState<EtatClient>({ statut: "chargement" });

  useEffect(() => {
    const controleur = new AbortController();
    apiClient
      .moi(controleur.signal)
      .then((identite) => setEtat({ statut: "connecte", identite }))
      .catch((erreur) => {
        if (erreur instanceof DOMException && erreur.name === "AbortError") return;
        setEtat({ statut: "anonyme" });
      });
    return () => controleur.abort();
  }, []);

  const connexion = useCallback(async (email: string, motDePasse: string) => {
    const identite = await apiClient.connexion(email, motDePasse);
    setEtat({ statut: "connecte", identite });
  }, []);

  const deconnexion = useCallback(async () => {
    try {
      await apiClient.deconnexion();
    } catch (erreur) {
      if (!(erreur instanceof ErreurApi)) throw erreur;
    }
    setEtat({ statut: "anonyme" });
  }, []);

  const valeur = useMemo(() => ({ ...etat, connexion, deconnexion }), [etat, connexion, deconnexion]);
  return <ContexteClient.Provider value={valeur}>{children}</ContexteClient.Provider>;
}

export function useSessionClient(): Contexte {
  const contexte = useContext(ContexteClient);
  if (!contexte) throw new Error("useSessionClient hors de FournisseurSessionClient");
  return contexte;
}
